import { motion } from 'framer-motion';
import {
  Wrench, Building2, Search, Filter,
  CheckCircle2, Clock, CalendarDays, Plus
} from 'lucide-react';

const MOCK_BOARD = {
  todo: [
    { id: 'REM-102', org: 'Bộ Nông nghiệp & PTNT', title: 'Thiếu trường dữ liệu Căn cước trong CSDL Hợp tác xã', deadline: '2026-03-25', severity: 'high', rulesViolated: 5 },
    { id: 'REM-104', org: 'UBND tỉnh Lào Cai', title: 'Chứng thư điện tử không đồng bộ hash string', deadline: '2026-03-22', severity: 'high', rulesViolated: 2 },
    { id: 'REM-105', org: 'Toà án tối cao', title: 'Data Encryption không đạt AES-256 nội bộ', deadline: '2026-03-30', severity: 'medium', rulesViolated: 1 },
  ],
  inProgress: [
    { id: 'REM-098', org: 'Bộ Công an', title: 'Khắc phục logic liên kết khoá ngoại (Foreign Key)', deadline: '2026-03-21', severity: 'medium', rulesViolated: 8, progress: 65 },
    { id: 'REM-099', org: 'Bộ Y tế', title: 'Cập nhật Schema HIS/PACS để map với CSDL Quốc gia', deadline: '2026-03-28', severity: 'critical', rulesViolated: 12, progress: 30 },
  ],
  done: [
    { id: 'REM-085', org: 'UBND TP. Hà Nội', title: 'Xoá mã nhúng độc hại nghi vấn tại Portal API', deadline: '2026-03-15', severity: 'critical', rulesViolated: 1, progress: 100, verified: true },
    { id: 'REM-082', org: 'Bộ Tài chính', title: 'Cập nhật lại certificate của API Gateway nội bộ', deadline: '2026-03-18', severity: 'high', rulesViolated: 1, progress: 100, verified: false },
  ]
};

const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Nghiêm trọng',
  high: 'Cao',
  medium: 'Trung bình',
};

const Column = ({ title, count, items, colorClass, borderClass }: { title: string, count: number, items: any[], colorClass: string, borderClass: string }) => (
  <div className="flex flex-col gap-4">
    <div className={`glass-panel p-3 border-t-2 rounded-xl flex justify-between items-center ${borderClass}`}>
      <h3 className="font-bold text-slate-900 dark:text-white text-sm">{title}</h3>
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${colorClass.replace('text-', 'bg-').replace('400', '500/20')} ${colorClass}`}>
        {count}
      </span>
    </div>

    <div className="flex-1 space-y-4">
      {items.map((item, idx) => (
        <motion.div
           initial={{ opacity: 0, y: 10 }}
           animate={{ opacity: 1, y: 0 }}
           transition={{ delay: idx * 0.1 }}
           key={item.id}
           className="glass-card p-4 group cursor-pointer hover:border-indigo-500/30"
        >
          <div className="flex justify-between items-start mb-2">
            <span className="text-[10px] font-bold font-mono px-2 py-0.5 bg-slate-200 dark:bg-black/40 text-slate-600 dark:text-slate-400 rounded border border-slate-300 dark:border-white/5">
              {item.id}
            </span>
            <span className={`text-[9px] font-bold tracking-tight px-2 py-0.5 rounded ${
              item.severity === 'critical' ? 'text-rose-600 dark:text-rose-400 bg-rose-500/10' : 
              item.severity === 'high' ? 'text-orange-600 dark:text-orange-400 bg-orange-500/10' : 
              'text-amber-600 dark:text-amber-400 bg-amber-500/10'
            }`}>
              {SEVERITY_LABELS[item.severity] || item.severity}
            </span>
          </div>
          
          <h4 className="text-sm font-bold text-slate-900 dark:text-slate-200 mb-3 group-hover:text-indigo-400 transition-colors leading-relaxed">
            {item.title}
          </h4>
          
          <div className="flex items-center gap-1.5 text-[11px] text-slate-600 dark:text-slate-400 mb-3">
            <Building2 size={12} />
            <span className="truncate">{item.org}</span>
          </div>

          {item.progress !== undefined && item.progress < 100 && (
            <div className="mb-3">
              <div className="flex justify-between text-[10px] font-bold text-slate-500 mb-1">
                <span>Tiến độ</span>
                <span className="text-indigo-600 dark:text-indigo-400">{item.progress}%</span>
              </div>
              <div className="h-1.5 w-full bg-slate-200 dark:bg-black/40 rounded-full overflow-hidden">
                <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${item.progress}%` }} />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-white/5">
            <div className="flex items-center gap-1 text-[10px] text-slate-500">
               <CalendarDays size={12} />
               {item.deadline}
            </div>
            
            <div className="flex items-center gap-2">
               {item.progress === 100 && (
                  item.verified ? (
                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1 font-bold">
                      <CheckCircle2 size={12} /> Đã duyệt
                    </span>
                  ) : (
                    <span className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1 font-bold">
                      <Clock size={12} /> Chờ duyệt
                    </span>
                  )
               )}
               <div className="flex -space-x-2">
                 <div className="w-5 h-5 rounded-full bg-indigo-500/20 border border-indigo-500/50 flex items-center justify-center text-[8px] font-bold text-indigo-400">
                    {item.rulesViolated}
                 </div>
               </div>
            </div>
          </div>
        </motion.div>
      ))}

      {title === 'Chưa xử lý' && (
         <button className="w-full glass-card border-dashed border-slate-300 dark:border-white/20 p-3 flex items-center justify-center gap-2 text-slate-500 hover:text-slate-900 dark:hover:text-white hover:border-slate-400 dark:hover:border-white/50 transition-colors text-xs font-bold">
            <Plus size={14} /> Thêm ticket thủ công
         </button>
      )}
    </div>
  </div>
);

export default function GlobalRemediationsPage() {
  return (
    <div className="space-y-8 animate-fade-in relative pb-10 h-full flex flex-col">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 shrink-0">
         <div>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-3 font-heading">
              <Wrench className="text-amber-500 dark:text-amber-400" size={32} />
              Quản lý kiến nghị khắc phục
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mt-2 max-w-2xl font-bold">
              Không gian làm việc dạng Kanban theo dõi tiến độ sửa chữa các vi phạm luật dữ liệu từ đơn vị trên toàn quốc.
            </p>
         </div>
         <div className="flex gap-3">
            <div className="flex items-center gap-2 border rounded-xl px-3 py-2 w-56 transition-all bg-white border-slate-200 focus-within:border-indigo-500 dark:bg-black/20 dark:border-white/5 dark:focus-within:border-indigo-500/50">
              <Search size={14} className="text-slate-500" />
              <input type="text" placeholder="Tìm kiếm ticket..." className="bg-transparent text-xs outline-none w-full text-slate-900 dark:text-white" />
            </div>
            <button className="btn-outline flex items-center gap-2">
               <Filter size={16} /> Lọc
            </button>
            <button className="btn-futuristic">
               Xuất báo cáo tiến độ
            </button>
         </div>
      </div>

      {/* KANBAN BOARD */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-6 items-start mt-4">
        <Column 
          title="Chưa xử lý" 
          count={MOCK_BOARD.todo.length} 
          items={MOCK_BOARD.todo} 
          colorClass="text-rose-400"
          borderClass="border-t-rose-500/50 bg-rose-500/5"
        />
        <Column 
          title="Đang khắc phục" 
          count={MOCK_BOARD.inProgress.length} 
          items={MOCK_BOARD.inProgress} 
          colorClass="text-amber-400"
          borderClass="border-t-amber-500/50 bg-amber-500/5"
        />
        <Column 
          title="Đã hoàn thành" 
          count={MOCK_BOARD.done.length} 
          items={MOCK_BOARD.done} 
          colorClass="text-emerald-400"
          borderClass="border-t-emerald-500/50 bg-emerald-500/5"
        />
      </div>
    </div>
  );
}
